// The bank's blueprints (RFC-056 §4): what a kind of candidate is examined
// on, as RFC-055 writes it in EDN. Everyone sees those published, with
// what each draws, and may try one — a draw that delivers nothing, for a
// class of theirs — before asking for a paper; an admin writes them, a new
// version for each change, and publishes the one drawn from. A list of
// rows and a page of each (byj-1wv.51.18), as the items are: a blueprint is
// what is acted on — published, written again, tried, asked for — and its
// EDN is read at the page's width, never in a drawer's.
import { useCallback, useEffect, useRef, useState } from "react";
import type { BankClient, BankMe, Blueprint } from "../bank";
import { type BlueprintForm, type Cannot, blankFields, fieldKeys, fromEdn, problems, toEdn } from "../blueprintForm";
import { t } from "../i18n";
import { navigate } from "../router";
import { BankBlueprintFields } from "./BankBlueprintFields";
import { FormDialog } from "./FormDialog";
import { Tabs, panelId, tabId } from "./Tabs";
import { Link } from "./Nav";
import { RowMenu, type MenuItem } from "./RowMenu";
import { EmptyRows, Grouped } from "./Rows";
import { StateBar } from "./StateBar";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** What a blueprint draws, in a line. */
export function drawsLine(b: Blueprint): string {
  const s = b.summary;
  if (!s) return "";
  return [
    s.items && t("bank:bpItems", { n: s.items }),
    s.minutes && t("bank:minutes", { n: s.minutes }),
    s.pass_mark && t("bank:bpPassMark", { n: s.pass_mark }),
    s.approval && t("bank:bpApproval"),
  ]
    .filter(Boolean)
    .join(" · ");
}

// a key a line, as the editor's hint says it is written, and each one the
// key list explains: what a person changes is one value on one line
// (in the order the fields write a blueprint: share/testdata/blueprints/cafe-staff.edn)
export const example = `{:id "cafe-staff"
 :title "Working with Claude Code in a café's back office"
 :audience :entry
 :minutes 40
 :items 4
 :coverage {:delegation 0.1 :description 0.2 :discernment 0.35 :diligence 0.35}
 :difficulty {:easy 0.75 :hard 0.25}
 :require [:trap :guard]
 :knowledge {:items 1 :weight 0.1 :minutes 2}
 :pass-mark 70
 :tolerance 0.1
 :agents [:claude]
 :model "sonnet"}`;

/** The keys of a blueprint (RFC-055): those the fields hold, in the order they write them (the example's), then those kept as text. */
const bpKeys = [...fieldKeys, "tolerance", "agents", "model"] as const;

/** An admin's editor of a blueprint's EDN: a new one, or the next version of `of`. */
/** The key a line of the EDN sets: its first `:key` (`:minutes 40` → minutes), when it is one a blueprint has. */
export function keyOfLine(text: string, at: number): string | null {
  const start = text.lastIndexOf("\n", at - 1) + 1;
  const end = text.indexOf("\n", at);
  const line = text.slice(start, end === -1 ? undefined : end);
  const m = /:([a-z][\w-]*)/.exec(line);
  return m && (bpKeys as readonly string[]).includes(m[1]!) ? m[1]! : null;
}

type EditView = "fields" | "text";

/** Why the fields cannot hold a blueprint, in the editor's words. */
const cannotWords = (why: Cannot) => (why.kind === "read" ? t("bank:bpCannot.read", { at: why.at + 1 }) : why.kind === "notMap" ? t("bank:bpCannot.notMap") : t(`bank:bpCannot.${why.kind}`, { key: why.key }));

/**
 * An admin's editor of a blueprint: a new one, or the next version of `of`,
 * in two views of one blueprint (byj-1wv.90). Its fields, the default: the
 * keys an admin sets most, the others kept as written. Its EDN text: the box
 * on the left, every key's meaning on the right at once — a line each, never
 * a list to scroll — the key on the caret's line marked; under the box on a
 * narrow screen. Text the fields cannot hold keeps the editor on the text,
 * saying why. Either view saves one EDN.
 */
function EditDialog({ of, onSubmit, onClose }: { of?: Blueprint; onSubmit: (edn: string) => Promise<void>; onClose: () => void }) {
  const [first] = useState(() => {
    const text = of?.edn ?? example;
    return { text, read: fromEdn(text) };
  });
  const [view, setView] = useState<EditView>(first.read.ok ? "fields" : "text");
  const [form, setForm] = useState<BlueprintForm>(first.read.ok ? first.read.form : { fields: blankFields(), rest: [] });
  // the text the fields were read from, and what they read: kept as written until a field changes
  const [base, setBase] = useState<{ text: string; form: BlueprintForm } | null>(first.read.ok ? { text: first.text, form: first.read.form } : null);
  const [edn, setEdn] = useState(first.text);
  const [why, setWhy] = useState<Cannot | null>(first.read.ok ? null : first.read.why);
  const [current, setCurrent] = useState<string | null>(null);
  const follow = (el: HTMLTextAreaElement) => setCurrent(keyOfLine(el.value, el.selectionStart));
  const unchanged = base !== null && JSON.stringify(form) === JSON.stringify(base.form);
  // what the fields save: the text as written while nothing in them has changed, else the EDN they write
  const written = unchanged ? base!.text : toEdn(form);
  // whether a change in the fields would write the text afresh, losing its comments or its order: said before it is made
  const rewrites = base !== null && toEdn(base.form) !== base.text.trim();
  const show = (next: EditView) => {
    if (next === view) return;
    if (next === "text") {
      setEdn(written);
      setWhy(null);
      setView("text");
      return;
    }
    // the text read into the fields, or kept where it is, saying what the fields cannot hold
    const r = fromEdn(edn);
    if (!r.ok) return setWhy(r.why);
    setForm(r.form);
    setBase({ text: edn, form: r.form });
    setWhy(null);
    setView("fields");
  };
  const issues = view === "fields" ? problems(form.fields) : [];
  return (
    <FormDialog
      id="bank-blueprint-dialog"
      testid="bank-blueprint-edit"
      className="bank-bp-modal"
      title={of ? t("bank:bpEditTitle", { title: of.title }) : t("bank:bpNewTitle")}
      submitLabel={t("bank:bpSave")}
      canSubmit={view === "fields" ? issues.length === 0 : edn.trim() !== ""}
      onSubmit={() => onSubmit(view === "fields" ? written : edn)}
      onClose={onClose}
    >
      <p className="muted small flush">{of ? t("bank:bpEditLead") : t("bank:bpNewLead")}</p>
      <Tabs
        prefix="bank-bp-view"
        label={t("bank:bpViewLabel")}
        value={view}
        onChange={show}
        tabs={[
          { id: "fields", label: t("bank:bpViewFields") },
          { id: "text", label: t("bank:bpViewText") },
        ]}
      />
      {why && (
        <p className="warn small flush" role="status" data-testid="bank-blueprint-why">
          {t("bank:bpFieldsCannot", { why: cannotWords(why) })}
        </p>
      )}
      <div role="tabpanel" id={panelId("bank-bp-view", "fields")} aria-labelledby={tabId("bank-bp-view", "fields")} hidden={view !== "fields"}>
        {view === "fields" && rewrites && (
          <p className="muted small flush" data-testid="bank-blueprint-rewrites">
            {t("bank:bpFieldsRewrites")}
          </p>
        )}
        {view === "fields" && <BankBlueprintFields form={form} onChange={setForm} issues={issues} />}
      </div>
      <div role="tabpanel" id={panelId("bank-bp-view", "text")} aria-labelledby={tabId("bank-bp-view", "text")} hidden={view !== "text"}>
        <div className="bank-bp-editor">
          <label className="field">
            {t("bank:bpEdn")}
            <textarea
              className="mono"
              value={edn}
              onChange={(e) => {
                setEdn(e.target.value);
                // what the fields could not hold was the text before this change
                setWhy(null);
                follow(e.target);
              }}
              onSelect={(e) => follow(e.currentTarget)}
              onClick={(e) => follow(e.currentTarget)}
              onKeyUp={(e) => follow(e.currentTarget)}
              rows={16}
              aria-label={t("bank:bpEdn")}
              spellCheck={false}
            />
            {/* how it is written, the same for a new one and a new version */}
            <span className="muted small">{t("bank:bpFormatHint")}</span>
          </label>
          <section className="bank-bp-keys" aria-labelledby="bank-bp-keys-title" data-testid="bank-blueprint-keys">
            <h4 id="bank-bp-keys-title" className="small flush">
              {t("bank:bpKeysTitle")}
            </h4>
            <dl className="small">
              {bpKeys.map((k) => (
                <div key={k} data-key={k} className={k === current ? "is-current" : undefined} aria-current={k === current ? "true" : undefined}>
                  <dt>
                    <code>:{k}</code>
                  </dt>
                  <dd className="muted">{t(`bank:bpKeys.${k}`)}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
      </div>
    </FormDialog>
  );
}

/**
 * A trial draw of `b` for a class of the person's: on a blueprint's page,
 * the class picked here; in the request dialog, `fixed`, the class the
 * dialog has picked, and the trial a line of it (`compact`).
 */
export function Trial({ client, b, classes, fixed, compact = false }: { client: BankClient; b: Blueprint; classes: BankMe["user"]["classes"]; fixed?: string; compact?: boolean }) {
  const [picked, setKlass] = useState(classes[0]?.id ?? "");
  const klass = fixed ?? picked;
  const [result, setResult] = useState<{ drew: boolean; report: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // the waiting for a trial's result stops when the trial leaves the page: the
  // request dialog closed, or another blueprint or class picked in it
  const gone = useRef(false);
  useEffect(() => {
    gone.current = false;
    return () => {
      gone.current = true;
    };
  }, []);
  const run = async () => {
    setBusy(true);
    setResult(null);
    setError(null);
    try {
      const { job } = await client.trial(b.id, klass || undefined);
      for (let i = 0; i < 600 && !gone.current; i++) {
        const r = await client.trialResult(job);
        if (gone.current) return;
        if (r.state === "done" && r.output) return setResult(r.output);
        if (r.state === "failed") return setError(r.error);
        await new Promise((res) => setTimeout(res, 1000));
      }
    } catch (err) {
      if (!gone.current) setError(errText(err));
    } finally {
      if (!gone.current) setBusy(false);
    }
  };
  return (
    <section className={compact ? "stack tight" : "card pad stack"} data-testid="bank-trial">
      {!compact && <h3 className="rows-head flush">{t("bank:bpTrialHead")}</h3>}
      <p className="muted small flush">{t("bank:bpTrialLead")}</p>
      <div className="row wrap">
        {fixed === undefined && classes.length > 0 && (
          <label className="field bank-inline-field">
            {t("bank:reqClass")}
            {/* held while a trial runs, and a result put away when another class is picked: it was that class's */}
            <select
              value={klass}
              disabled={busy}
              onChange={(e) => {
                setKlass(e.target.value);
                setResult(null);
                setError(null);
              }}
              aria-label={t("bank:reqClass")}
            >
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <button type="button" className={compact ? "small" : "bank-inline-go"} disabled={busy} onClick={() => void run()}>
          {busy ? t("bank:bpTrying") : t("bank:bpTry")}
        </button>
      </div>
      {error && <p className="error small flush">{t("common:error", { m: error })}</p>}
      {result && (
        <>
          <p className={`flush ${result.drew ? "ok-text" : "error"}`}>{result.drew ? t("bank:bpDrew") : t("bank:bpDrewNot")}</p>
          <pre className="small job-log">{result.report}</pre>
        </>
      )}
    </section>
  );
}

const askPaper = (b: Blueprint) => navigate(`/bank/request?blueprint=${encodeURIComponent(b.id)}`);

function BlueprintRow({ b, admin }: { b: Blueprint; admin: boolean }) {
  const menu: MenuItem[] = b.published > 0 ? [{ label: t("bank:reqAsk"), onSelect: () => askPaper(b) }] : [];
  return (
    <li className={`share-row ${b.published ? "is-live" : "is-ended"}`} data-testid="bank-blueprint">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        bp
      </span>
      <div className="row-main">
        <Link to={`/bank/blueprints/${encodeURIComponent(b.id)}`} className="row-title">
          {b.title}
        </Link>
        <div className="row-sub">{drawsLine(b)}</div>
      </div>
      <div className="row-meta">
        {admin && b.latest > b.published && b.published > 0 && <span className="chip">{t("bank:bpDraft", { v: b.latest })}</span>}
        <span className={`chip${b.published ? " chip-live" : ""}`}>{b.published ? t("bank:bpPublished", { v: b.published }) : t("bank:bpUnpublished")}</span>
      </div>
      <RowMenu label={t("shares:moreFor", { title: b.title })} items={menu} />
    </li>
  );
}

export function BankBlueprints({ client, me }: { client: BankClient; me: BankMe }) {
  const admin = me.role === "admin";
  const [list, setList] = useState<Blueprint[] | null>(null);
  const [making, setMaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    client.blueprints().then(setList, (err: unknown) => setError(errText(err)));
  }, [client]);
  const all = list ?? [];
  return (
    <div data-testid="bank-blueprints">
      <div className="panel-head">
        <p className="muted small panel-lead">{admin ? t("bank:bpLeadAdmin") : t("bank:bpLead")}</p>
        {admin && (
          <button className="primary" aria-haspopup="dialog" aria-controls={making ? "bank-blueprint-dialog" : undefined} onClick={() => setMaking(true)}>
            {t("bank:bpNew")}
          </button>
        )}
      </div>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {list === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : (
        <Grouped<Blueprint>
          id="bank-blueprints"
          groups={[
            { title: t("bank:bpPublishedHead"), key: "published", items: all.filter((b) => b.published > 0), none: <EmptyRows text={admin ? t("bank:bpNoneAdmin") : t("bank:bpNone")} /> },
            { title: t("bank:bpUnpublishedHead"), key: "unpublished", items: all.filter((b) => !b.published) },
          ]}
          row={(b) => <BlueprintRow key={b.id} b={b} admin={admin} />}
        />
      )}
      {making && (
        <EditDialog
          onSubmit={async (edn) => {
            const b = await client.createBlueprint(edn);
            navigate(`/bank/blueprints/${encodeURIComponent(b.id)}`);
          }}
          onClose={() => setMaking(false)}
        />
      )}
    </div>
  );
}

/** A blueprint's page: how it stands, what it draws, its EDN, publishing and versions for an admin, and a trial draw. */
export function BankBlueprintPage({ client, me, id }: { client: BankClient; me: BankMe; id: string }) {
  const admin = me.role === "admin";
  const [b, setB] = useState<Blueprint | null>(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setB(await client.blueprint(id));
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
  }, [client, id]);
  useEffect(() => {
    void load();
  }, [load]);
  if (!b) return error ? <p className="error">{t("common:error", { m: error })}</p> : <p className="muted">{t("common:loading")}</p>;
  const publish = async (v: number) => {
    try {
      setB(await client.publishBlueprint(b.id, v));
      setDone(v ? t("bank:bpDidPublish", { v }) : t("bank:bpDidWithdraw"));
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
  };
  const draft = b.latest !== b.published;
  return (
    <section className="stack bank-item" data-testid="bank-blueprint-page">
      {/* an instructor reads a blueprint from where they ask for a paper, and goes back there */}
      <p className="small flush">
        <Link to={admin ? "/bank/blueprints" : "/bank/papers"} className="muted">
          ← {admin ? t("bank:backToBlueprints") : t("bank:backToPapers")}
        </Link>
      </p>
      <header className="page-head bank-item-head">
        <h2 className="flush">{b.title}</h2>
        <RowMenu label={t("shares:moreFor", { title: b.title })} items={admin && b.published > 0 ? [{ label: t("bank:bpWithdraw"), onSelect: () => void publish(0), danger: true }] : []} />
      </header>
      <StateBar
        className="exam-bar"
        label={t("bank:bpBar")}
        state={b.published ? "live" : "paused"}
        stateLabel={<span className="share-bar-state">{b.published ? t("bank:bpPublished", { v: b.published }) : t("bank:bpUnpublished")}</span>}
        testid="bank-blueprint-bar"
        actions={
          <>
            {b.published > 0 && (
              <button className="small primary" onClick={() => askPaper(b)}>
                {t("bank:reqAsk")}
              </button>
            )}
            {admin && draft && (
              <button className={`small${b.published ? "" : " primary"}`} onClick={() => void publish(b.latest)}>
                {t("bank:bpPublish", { v: b.latest })}
              </button>
            )}
            {admin && (
              <button className="small" aria-haspopup="dialog" aria-controls={editing ? "bank-blueprint-dialog" : undefined} onClick={() => setEditing(true)}>
                {t("bank:bpNewVersion")}
              </button>
            )}
          </>
        }
      >
        <span className="share-bar-facts">
          {[drawsLine(b), admin && draft && b.published > 0 && t("bank:bpDraft", { v: b.latest })].filter(Boolean).join(" · ")}
        </span>
      </StateBar>
      {admin && !b.published && <p className="muted small flush">{t("bank:bpNotPublishedNote")}</p>}
      {error && <p className="error flush">{t("common:error", { m: error })}</p>}
      {done && (
        <p className="ok-text flush" role="status">
          {done}
        </p>
      )}
      <Trial client={client} b={b} classes={me.user.classes} />
      {b.edn && (
        <section className="stack tight">
          <h3 className="rows-head">{t("bank:bpEdn")}</h3>
          <pre className="small mono job-log bank-edn">{b.edn}</pre>
        </section>
      )}
      {editing && (
        <EditDialog
          of={b}
          onSubmit={async (edn) => {
            setB(await client.newBlueprintVersion(b.id, edn));
            setEditing(false);
            setDone(t("bank:bpDidVersion"));
          }}
          onClose={() => setEditing(false)}
        />
      )}
    </section>
  );
}
